// Story 7.25 — the user-terms staleness gate, SPA side.
//
// The backend (Story 1.14) 426s EVERY gated endpoint the moment the active
// user terms move past the version a session snapshotted. Detection therefore
// cannot live in any one hook — the first 426 can surface from account-me,
// the feed, the vault, anywhere. This module is the single flag those
// detections raise, and <TosReacceptDialog /> is its only consumer.
//
// Deliberately a hand-rolled useSyncExternalStore module and not a query
// cache entry: the flag must survive `queryClient.clear()` (which the
// re-accept flow itself triggers) and must never be refetched, garbage-
// collected, or devalued as "stale" — it is UI state, not server state.

import { useSyncExternalStore } from "react";

let stale = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** Raise the gate. Called from the QueryCache/MutationCache error handlers
 *  in App.tsx on any 426 / TOS_ACCEPTANCE_REQUIRED response. Idempotent —
 *  every gated query fails at once when the gate fires, so this is called
 *  in bursts. */
export function notifyTosStale(): void {
  if (stale) return;
  stale = true;
  emit();
}

/** Lower the gate — after a successful accept, or on logout (the next
 *  account to sign in on this tab may be current). */
export function clearTosStale(): void {
  if (!stale) return;
  stale = false;
  emit();
}

/** The backend's staleness rejection, from any error shape the SPA's hooks
 *  throw. Structured hooks (account-me, sign-in, …) carry the envelope
 *  `code` and the `status`; the code check is first-class, not a fallback —
 *  426 is this gate's status today, but the contract the backend commits to
 *  is the error code. The message match covers the hooks that throw a bare
 *  `new Error(\`… (HTTP ${status})\`)` (useFeed and friends): brittle in
 *  isolation, but it is the third net, never the only one. */
export function isTosStaleError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const e = error as { status?: unknown; code?: unknown; message?: unknown };
  if (e.code === "TOS_ACCEPTANCE_REQUIRED" || e.status === 426) return true;
  return typeof e.message === "string" && e.message.includes("(HTTP 426)");
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return stale;
}

export function useTosStale(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
